<template>
  <div :class="computedClasses">
    <slot :get-item-class="getItemClass">
      <div
        v-if="safeMaxResults === 0 && !hideNoResults"
        class="border-1 border-gray-300 rounded-lg p-4 text-center font-medium"
      >
        {{ t('noResultsFound') }}
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
type GridCols = 1 | 2 | 3 | 4 | 5 | 6

const props = withDefaults(defineProps<{
  /** Number of items; use 0 when data is loading or empty to avoid NaN in layout. */
  maxResults?: number

  cols?: GridCols
  smCols?: GridCols
  mdCols?: GridCols
  lgCols?: GridCols
  xlCols?: GridCols
  xxlCols?: GridCols

  rows?: number
  smRows?: number
  mdRows?: number
  lgRows?: number
  xlRows?: number
  xxlRows?: number

  hideNoResults?: boolean
}>(), {
  maxResults: 0,
  cols: 1,
})

const { t } = useI18n()

function visibleCount(rows: number, cols: GridCols) {
  return rows * cols
}

type VisibleCounts = {
  count: number
  countSm: number
  countMd: number
  countLg: number
  countXl: number
  count2xl: number
}

const breakpointConfig = [
  { rowProp: 'rows', colProp: 'cols', key: 'count' },
  { rowProp: 'smRows', colProp: 'smCols', key: 'countSm' },
  { rowProp: 'mdRows', colProp: 'mdCols', key: 'countMd' },
  { rowProp: 'lgRows', colProp: 'lgCols', key: 'countLg' },
  { rowProp: 'xlRows', colProp: 'xlCols', key: 'countXl' },
  { rowProp: 'xxlRows', colProp: 'xxlCols', key: 'count2xl' },
] as const

const safeMaxResults = computed(() => Number(props.maxResults) || 0)

const visibleCounts = computed((): VisibleCounts => {
  const maxResults = safeMaxResults.value
  let resolvedRows = 0
  let resolvedCols = props.cols
  const result: VisibleCounts = {
    count: 0,
    countSm: 0,
    countMd: 0,
    countLg: 0,
    countXl: 0,
    count2xl: 0,
  }

  for (const { rowProp, colProp, key } of breakpointConfig) {
    resolvedRows = props[rowProp] ?? resolvedRows
    resolvedCols = props[colProp] ?? resolvedCols
    const count = resolvedRows === 0 ? maxResults : visibleCount(resolvedRows, resolvedCols)
    result[key] = Math.min(count, maxResults)
  }

  return result
})

function getItemClass(index: number) {
  const c = visibleCounts.value
  return {
    'hidden': index >= c.count,
    'block': index < c.count,

    'sm:hidden': index >= c.countSm,
    'sm:block': index < c.countSm,

    'md:hidden': index >= c.countMd,
    'md:block': index < c.countMd,

    'lg:hidden': index >= c.countLg,
    'lg:block': index < c.countLg,

    'xl:hidden': index >= c.countXl,
    'xl:block': index < c.countXl,

    '2xl:hidden': index >= c.count2xl,
    '2xl:block': index < c.count2xl,
  }
}

const computedColsClasses = computed(() => {
  return {
    'grid-cols-1': props.cols === 1,
    'grid-cols-2': props.cols === 2,
    'grid-cols-3': props.cols === 3,
    'grid-cols-4': props.cols === 4,
    'grid-cols-5': props.cols === 5,
    'grid-cols-6': props.cols === 6,

    'sm:grid-cols-1': props.smCols === 1,
    'sm:grid-cols-2': props.smCols === 2,
    'sm:grid-cols-3': props.smCols === 3,
    'sm:grid-cols-4': props.smCols === 4,
    'sm:grid-cols-5': props.smCols === 5,
    'sm:grid-cols-6': props.smCols === 6,

    'md:grid-cols-1': props.mdCols === 1,
    'md:grid-cols-2': props.mdCols === 2,
    'md:grid-cols-3': props.mdCols === 3,
    'md:grid-cols-4': props.mdCols === 4,
    'md:grid-cols-5': props.mdCols === 5,
    'md:grid-cols-6': props.mdCols === 6,

    'lg:grid-cols-1': props.lgCols === 1,
    'lg:grid-cols-2': props.lgCols === 2,
    'lg:grid-cols-3': props.lgCols === 3,
    'lg:grid-cols-4': props.lgCols === 4,
    'lg:grid-cols-5': props.lgCols === 5,
    'lg:grid-cols-6': props.lgCols === 6,

    'xl:grid-cols-1': props.xlCols === 1,
    'xl:grid-cols-2': props.xlCols === 2,
    'xl:grid-cols-3': props.xlCols === 3,
    'xl:grid-cols-4': props.xlCols === 4,
    'xl:grid-cols-5': props.xlCols === 5,
    'xl:grid-cols-6': props.xlCols === 6,

    '2xl:grid-cols-1': props.xxlCols === 1,
    '2xl:grid-cols-2': props.xxlCols === 2,
    '2xl:grid-cols-3': props.xxlCols === 3,
    '2xl:grid-cols-4': props.xxlCols === 4,
    '2xl:grid-cols-5': props.xxlCols === 5,
    '2xl:grid-cols-6': props.xxlCols === 6,
  }
})

const computedClasses = computed(() => {
  return [
    'grid gap-6',
    computedColsClasses.value,
  ]
})
</script>

<i18n src="~~/i18n/locales/common/search/results.json" lang="json" />
